import { useState } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, Globe, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const GROUP_LABEL = { resolver: 'Resolver rules (pure)', environment: 'Environment', ops: 'OPS connectivity', http: 'HTTP twins of the APP repro' };
const Icon = ({ status }) => status === 'pass'
  ? <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
  : status === 'warn' ? <AlertCircle className="h-4 w-4 text-amber-600 shrink-0" /> : <XCircle className="h-4 w-4 text-rose-600 shrink-0" />;

// V25 — one-click, production-safe smoke run for the country / pricing rules.
// Runs server-side against THIS deployment (no test accounts, nothing sent to OPS, no orders).
export const V25SmokeCard = () => {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setRunning(true);
    try {
      const res = await api.post('/api/admin/ops/v25-country-smoke');
      setResult(res.data);
      const { counts } = res.data;
      (counts.fail ? toast.error : counts.warn ? toast.warning : toast.success)(
        `V25 smoke: ${counts.pass} passed · ${counts.warn} warnings · ${counts.fail} failed`);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Smoke run failed');
    } finally {
      setRunning(false);
    }
  };

  const groups = result ? [...new Set(result.checks.map((c) => c.group))] : [];
  const verdictCls = { pass: 'bg-emerald-500/15 text-emerald-700', warn: 'bg-amber-500/15 text-amber-700', fail: 'bg-rose-500/15 text-rose-700' };

  return (
    <Card className="border-sky-500/40 bg-sky-500/5" data-testid="v25-smoke-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Globe className="h-5 w-5 text-sky-600" />
          V25 country &amp; pricing smoke (post-deploy)
        </CardTitle>
        <CardDescription>
          Runs the APP/OPS handover checks against <b>this</b> deployment: founder resolver cases,
          the APP team&apos;s <code className="text-xs bg-muted px-1 rounded">?country=IN</code> repro, INR-quote-from-abroad 409,
          legacy order path retired, consult pricing, CORS, OPS pricing feed, OPS override route + signing.
          Safe on production — no test accounts, nothing sent to OPS, no orders created.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={run} disabled={running} data-testid="v25-smoke-run-btn">
            {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Globe className="h-4 w-4 mr-2" />}
            Run V25 smoke
          </Button>
          {result && (
            <>
              <Badge className={verdictCls[result.verdict]} data-testid="v25-smoke-verdict">
                {result.verdict.toUpperCase()} · {result.counts.pass} pass · {result.counts.warn} warn · {result.counts.fail} fail
              </Badge>
              <span className="text-xs text-muted-foreground">
                {new Date(result.ran_at).toLocaleString()} · {result.environment?.public_base_url || result.environment?.app_domain || 'this pod'}
              </span>
            </>
          )}
        </div>
        {result && groups.map((g) => (
          <div key={g} data-testid={`v25-smoke-group-${g}`}>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">{GROUP_LABEL[g] || g}</p>
            <ul className="space-y-1">
              {result.checks.filter((c) => c.group === g).map((c) => (
                <li key={c.name} className="flex items-start gap-2 text-sm" data-testid={`v25-smoke-check-${c.status}`}>
                  <Icon status={c.status} />
                  <span className="min-w-0">
                    <span className={c.status === 'fail' ? 'text-rose-700 font-medium' : ''}>{c.name}</span>
                    {c.detail && <span className="block text-[11px] text-muted-foreground break-words">{c.detail}</span>}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
